import { Commit, Dispatch, Module } from "vuex";
import { RootState } from "@/store";
import {
  getWalletBalance,
  withdraw,
  getTransactionDetail,
  TransactionDetailParams,
  goAuthentication,
  AuthenticationParams,
  queryUserInfo,
  TradedParams,
  getTradedList,
  getFirmTradedList,
} from "@/api/userCenter";
import { getSalesOrderList } from "@/api/apiDataset";
import { formatNum, milliFormat } from "@/utils/format";
import dayjs from "dayjs";
import {
  SalesOrderApiParams,
  SalesOrderApiResponse,
} from "@/api/apiDataset/types";

type TradeItem = {
  code: string;
  type: string;
  status: string;
  amount1: number;
  time: string;
  priceinfo: {
    dataPrice1: number;
    programPrice1: number;
    feePrice1: number;
  };
  endTime: string;
  datasetName: string;
  serviceCharge: number;
  createUser: string;
  bankName: string;
  bankCode: string;
};
interface State {
  walletInfo: {
    available: number;
    frozen: number;
    withDrawable: number;
  };
  tradeList: TradeItem[];
  tradeListPage: {
    count: number;
    pageNo: number;
    pageSize: number;
    totalPage: number;
  };
  tradedList: TradeItem[];
  tradedListPage: {
    count: number;
    pageNo: number;
    pageSize: number;
    totalPage: number;
  };
  apiTradedList: SalesOrderApiResponse[];
  apiTradedListPage: {
    count: number;
    pageNo: number;
    pageSize: number;
    totalPage: number;
  };
  firmTradedList: TradeItem[];
  firmTradedListPage: {
    count: number;
    pageNo: number;
    pageSize: number;
    totalPage: number;
  };
  authStatus: number;
  authError: string;
}

const store: Module<State, RootState> = {
  namespaced: true, // 为每个模块添加一个前缀名，保证模块命明不冲突
  state: () => {
    return {
      walletInfo: {
        available: 0,
        frozen: 0,
        withDrawable: 0,
      },
      tradeList: [],
      tradeListPage: {
        count: 0,
        pageNo: 1,
        pageSize: 10,
        totalPage: 0,
      },
      tradedList: [],
      tradedListPage: {
        count: 0,
        pageNo: 1,
        pageSize: 12,
        totalPage: 0,
      },
      apiTradedList: [],
      apiTradedListPage: {
        count: 0,
        pageNo: 1,
        pageSize: 12,
        totalPage: 0,
      },
      firmTradedList: [],
      firmTradedListPage: {
        count: 0,
        pageNo: 1,
        pageSize: 12,
        totalPage: 0,
      },
      authError: "",
      authStatus: 0, // 0 未认证 1 已认证 2 认证中 3 认证失败
    };
  },
  mutations: {
    setWalletInfo(state, payload) {
      state.walletInfo = payload;
    },
    setTradeList(state, payload) {
      state.tradeList = payload.data.map((item: any) => {
        return {
          ...item,
          amount1: milliFormat(formatNum(item.amount1)),
          amount2: milliFormat(formatNum(item.amount2)),
          time: item.time
            ? dayjs(item.time).format("YYYY/MM/DD HH:mm:ss")
            : "--",
          endTime: item.endTime
            ? dayjs(item.endTime).format("YYYY/MM/DD HH:mm:ss")
            : "--",
        };
      });
      state.tradeListPage = {
        count: payload.page.count,
        pageNo: payload.page.pageNo,
        pageSize: payload.page.pageSize,
        totalPage: payload.page.totalPage,
      };
    },
    setTradedList(state, payload) {
      if (!payload) {
        state.tradedList = [];
        state.tradedListPage = {
          count: 0,
          pageNo: 1,
          pageSize: 12,
          totalPage: 0,
        };

        return;
      }
      state.tradedList = payload.data.map((item: any) => {
        return {
          ...item,
          amount1: milliFormat(formatNum(item.amount1)),
          amount2: milliFormat(formatNum(item.amount2)),
          time: item.time
            ? dayjs(item.time).format("YYYY/MM/DD HH:mm:ss")
            : "--",
          endTime: item.endTime
            ? dayjs(item.endTime).format("YYYY/MM/DD HH:mm:ss")
            : "--",
        };
      });
      state.tradedListPage = {
        count: payload.page.count,
        pageNo: payload.page.pageNo,
        pageSize: payload.page.pageSize,
        totalPage: payload.page.totalPage,
      };
    },
    setApiTradedList(state, payload) {
      if (!payload) {
        state.apiTradedList = [];
        state.apiTradedListPage = {
          count: 0,
          pageNo: 1,
          pageSize: 12,
          totalPage: 0,
        };

        return;
      }
      state.apiTradedList = payload.data.map((item: any) => {
        return {
          ...item,
          amount1: milliFormat(formatNum(item.amount1)),
          amount2: milliFormat(formatNum(item.amount2)),
          time: item.time
            ? dayjs(item.time).format("YYYY/MM/DD HH:mm:ss")
            : "--",
          endTime: item.endTime
            ? dayjs(item.endTime).format("YYYY/MM/DD HH:mm:ss")
            : "--",
        };
      });
      state.apiTradedListPage = {
        count: payload.page.count,
        pageNo: payload.page.pageNo,
        pageSize: payload.page.pageSize,
        totalPage: payload.page.totalPage,
      };
    },
    setFirmTradedList(state, payload) {
      console.log("payload", payload);
      if (!payload) {
        state.firmTradedList = [];
        state.firmTradedListPage = {
          count: 0,
          pageNo: 1,
          pageSize: 12,
          totalPage: 0,
        };

        return;
      }
      state.firmTradedList = payload.data.map((item: any) => {
        return {
          ...item,
          price: milliFormat(formatNum(item.price)),
          time: item.createTimeSql
            ? dayjs(item.createTimeSql).format("YYYY/MM/DD HH:mm:ss")
            : "--",
        };
      });
      state.firmTradedListPage = {
        count: payload.page.count,
        pageNo: payload.page.pageNo,
        pageSize: payload.page.pageSize,
        totalPage: payload.page.totalPage,
      };
    },
    setAuthStatus(state, payload) {
      state.authStatus = payload;
    },
    setAuthError(state, payload) {
      state.authError = payload;
    },
  },
  actions: {
    async getWalletInfo({ commit }: { commit: Commit }) {
      try {
        const { available, frozen, withDrawable } = await getWalletBalance();
        commit("setWalletInfo", {
          available: milliFormat(formatNum(available)),
          frozen: milliFormat(formatNum(frozen)),
          withDrawable: milliFormat(formatNum(withDrawable)),
        });
      } catch (error) {
        log.error(error);
      }
    },
    async withdraw(
      { dispatch }: { dispatch: Dispatch },
      payload: {
        params: { amount?: number; token?: string };
        successCb: () => void;
        failCb: () => void;
      }
    ) {
      const { params, successCb, failCb } = payload;
      try {
        await withdraw(params);
        dispatch("getWalletInfo");
        successCb();
      } catch (error) {
        log.error(error);
        failCb();
      }
    },
    async getTransactionDetail(
      { commit }: { commit: Commit },
      params: TransactionDetailParams
    ) {
      try {
        const res = await getTransactionDetail(params); /** 获取交易详情 */
        commit("setTradeList", res);
      } catch (error) {
        log.error(error, "error");
      }
    },
    async getTradedDetail(
      { commit }: { commit: Commit },
      params: TradedParams
    ) {
      try {
        const res = await getTradedList(params);
        commit("setTradedList", res);
      } catch (error) {
        log.error(error, "error");
      }
    },
    async getApiTradedDetail(
      { commit }: { commit: Commit },
      params: SalesOrderApiParams
    ) {
      try {
        const res = await getSalesOrderList(params);
        commit("setApiTradedList", res);
      } catch (error) {
        log.error(error, "error");
      }
    },
    async getFirmDetail({ commit }: { commit: Commit }, params: TradedParams) {
      try {
        const res = await getFirmTradedList(params);
        commit("setFirmTradedList", res);
      } catch (error) {
        log.error(error, "error");
      }
    },
    async getAuthStatus({
      commit,
      dispatch,
    }: {
      commit: Commit;
      dispatch: Dispatch;
    }) {
      try {
        const res = await queryUserInfo();
        // requestStatus for backend: 0 轮询 3 成功 4失败
        // authStatus for frontend: 0, // 0 未认证 1 已认证 2 认证中 3 认证失败
        if (res.requestStatus === 3) {
          commit("setAuthStatus", 1);
        } else if (res.requestStatus === 0) {
          setTimeout(() => {
            dispatch("getAuthStatus");
          }, 10000);
        } else {
          commit("setAuthStatus", 3);
          commit("setAuthError", res.remark);
        }
      } catch (error) {
        log.error(error);
      }
    },
    async authentication(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      payload: AuthenticationParams
    ) {
      try {
        commit("setAuthStatus", 2);
        await goAuthentication(payload);
        dispatch("getAuthStatus");
      } catch (error: any) {
        commit("setAuthStatus", 3);
        commit("setAuthError", error.resultDesc);
        log.error(error);
      }
    },
  },
};

export default store;
